import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  Suspense,
  type CSSProperties,
  type Dispatch,
  type SetStateAction,
  type ReactElement,
} from 'react';
import { zertAblaufListe } from './lib/zertAblauf.js';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useT, I18nContext } from './i18n/index.jsx';
import { getThemeVars } from './config/theme.js';
import { startPath } from './config/routes.js';
import { AppProvider } from './contexts/AppContext.jsx';
import { retryLazy } from './lib/retryLazy.js';

// Hooks
import { useSettings } from './hooks/useSettings.js';
import { useAuth } from './hooks/useAuth.js';
import { useNotifications } from './hooks/useNotifications.js';
import { useSocial } from './hooks/useSocial.js';
import { useNavigation } from './hooks/useNavigation.js';
import { useRealtime } from './hooks/useRealtime.js';
import { db } from './lib/supabase.js';

// Pre-Auth Components (lazy — nur vor Login geladen)
const AuthScreen = retryLazy(() => import('./pages/auth/AuthScreen.jsx'));
const PasswordRecoveryForm = retryLazy(
  () => import('./pages/auth/PasswordRecovery.jsx'),
);
const EinladungAnnehmen = retryLazy(
  () => import('./pages/auth/EinladungAnnehmen.jsx'),
);
import { CookieBanner } from './components/pwa/CookieBanner.jsx';

// Layout (post-auth)
import { AppShell } from './components/layout/AppShell.jsx';
import { ProtectedRoutes } from './components/layout/ProtectedRoutes.jsx';
import { WartungsScreen } from './components/layout/WartungsScreen.jsx';
import { LoadingScreen } from './components/layout/LoadingScreen.jsx';
import { PendingDeletionScreen } from './components/layout/PendingDeletionScreen.jsx';

import { todayLocal } from './lib/format.js';
import type { Profile, ToastType } from './types.js';
import { ladePlattformSettings } from './lib/plattformSettings.js';
import { usePlattformSettingsStand } from './hooks/usePlattformSettingsStand.js';

// Lazy: Tracking (public route)
const TrackingPage = retryLazy(() => import('./pages/tracking/TrackingPage.jsx'));

// ═══════════════════════════════════════════════════════
// Lokale Types
// ═══════════════════════════════════════════════════════

interface ToastState {
  msg: string;
  type: ToastType | undefined;
  /** Laufende Nummer — jede neue Meldung startet ihre eigene Anzeigezeit (23.10.2026). */
  id: number;
}

interface ModalState {
  type: string | null;
  data: unknown;
}

/** Zertifikat-Warn-Eintrag für Ablauf-Erinnerung. */
interface ZertWarnung {
  key: string;
  typ: 'abgelaufen' | 'bald';
}

/**
 * App — Root-Component des DRPX Courier B2B-Logistik-Marktplatzes.
 *
 * Orchestriert: Auth (useAuth), Navigation (useNavigation), Notifications
 * (useNotifications), Settings (useSettings), Social (useSocial), Realtime
 * (useRealtime). Bündelt alle State und Callbacks in `AppContext`, sodass
 * Pages und AppShell via `useApp()` darauf zugreifen können.
 *
 * Routing: Public `/track/:token`, `/login`, sonst alles protected in `AppShell`.
 * Pre-Auth-Screens: Wartung, Loading, PasswordRecovery, PendingDeletion.
 */
export default function App(): ReactElement {
  const t = useT();
  const navigate = useNavigate();

  // ═══ Settings Hook ═══
  // NOTE: useSettings erwartet einen lokalen `I18nContext`-Typ mit
  // `setLang: (lang: string) => void`. `useT()` liefert aber `TFunction`
  // mit `setLang: (lang: LangCode) => void`. Cast via unknown (Pattern 25).
  const settings = useSettings(t as unknown as Parameters<typeof useSettings>[0]);
  const {
    darkMode,
    WARTUNG_PASSWORT,
    wartungFreigeschaltet,
    wartungInput,
    setWartungInput,
    wartungPruefen,
  } = settings;

  // ═══ Plattform-Einstellungen (18.10.2026) ═══
  // Preise, Impressum, AGB und Startseiten-Texte schon beim Öffnen laden —
  // auch ohne Anmeldung (vorher erst nach dem Login: neue Besucher sahen
  // Standardwerte). Der Stand-Hook zeichnet die App neu, sobald sie da sind.
  usePlattformSettingsStand();
  useEffect(() => {
    void ladePlattformSettings();
  }, []);

  // ═══ Toast / Modal state (needed by multiple hooks) ═══
  const [toast, setToast] = useState<ToastState | null>(null);
  const [modal, setModal] = useState<ModalState>({ type: null, data: null });
  const toastZaehler = useRef(0);
  const openToast = useCallback((msg: string, type?: string) => {
    toastZaehler.current += 1;
    setToast({ msg, type: type as ToastType | undefined, id: toastZaehler.current });
  }, []);
  const openModal = useCallback((type: string, data?: unknown) => {
    setModal({ type, data });
  }, []);

  // ═══ Auth Hook ═══
  const navRef = useRef<((tab: string) => void) | null>(null);
  const auth = useAuth({
    t,
    nav: (tab: string) => {
      if (navRef.current) navRef.current(tab);
    },
    // NOTE: Prop heißt `_setOnboardingStep` (Pattern 14 — dead prop mit
    // Underscore-Prefix). Der Hook nutzt sie intern nicht, aber die Signatur
    // behalten wir bei, um Call-Site-Änderungen zu vermeiden.
    _setOnboardingStep: (s: number) => {
      settings.setOnboardingStep(s);
    },
    openToast,
  });

  // Derived user
  const user = useMemo<Profile | null>(
    () =>
      auth.currentUser
        ? {
            ...(auth.users.find((u) => u.id === auth.currentUser!.id) || {}),
            ...auth.currentUser,
          }
        : null,
    [auth.currentUser, auth.users],
  );

  // ═══ Navigation Hook ═══
  const navigation = useNavigation({
    user,
    auftraege: auth.auftraege,
    bewertungen: auth.bewertungen,
    users: auth.users,
    t,
    pendingZertsTotal: auth.pendingZertsTotal,
    openModal,
  });
  navRef.current = navigation.nav;

  // ═══ Notifications Hook ═══
  const notifs = useNotifications({
    user,
    users: auth.users,
    setUsers: auth.setUsers,
    setCurrentUser: auth.setCurrentUser,
    nav: navigation.nav,
    loadData: auth.loadData,
    t,
    emailNotifs: settings.emailNotifs,
  });

  // ═══ Social Hook (Rote Liste + Favoriten) ═══
  const social = useSocial(user, auth.setUsers, auth.setCurrentUser, t, openToast, openModal);

  // ═══ Realtime: Aufträge + Angebote (mit Security-Filter) ═══
  useRealtime({ user, setAuftraege: auth.setAuftraege });

  // ═══ Dringende Aufträge: Warnung bei Abholung < 2h ohne Fahrer ═══
  // Bewusst nur Chef und Admin (16.10.2026): Die Warnung geht als
  // Benachrichtigung ans Firmenkonto. Liefe sie auch bei Mitarbeitern, bekäme
  // der Chef sie von jedem Gerät seines Teams. Benachrichtigungen pro Zugang
  // kommen in einer späteren DRPX-Pro-Phase.
  useEffect(() => {
    if (!user || (user.rolle !== 'admin' && user.rolle !== 'firma')) return;
    const check = (): void => {
      const jetzt = Date.now();
      const auftr = auth.auftraege || [];
      auftr.forEach((a) => {
        if (a.status !== 'offen' && a.status !== 'angebote') return;
        if (!a.datum) return;
        if (user.rolle === 'firma' && a.firmaId !== user.id) return;
        const abholMs = new Date(a.datum + 'T' + (a.zeit || '23:59')).getTime();
        const diffMs = abholMs - jetzt;
        if (diffMs > 0 && diffMs < 2 * 60 * 60 * 1000) {
          const warnKey =
            'drpx_dringend_' + a.id + '_' + new Date().toISOString().slice(0, 13);
          if (sessionStorage.getItem(warnKey)) return;
          sessionStorage.setItem(warnKey, '1');
          const titleKey = user.rolle === 'admin' ? 'dringendTitle' : 'dringendFirmaTitle';
          const body = (a.nr || a.titel || '') + ' — ' + (a.zeit || a.datum);
          // BUG #35 FIX (Original-JS): Code rief `notifications.pushNotif(...)` auf,
          // aber das Objekt heißt im Scope `notifs`. Im JS blieb das latent
          // (hätte ReferenceError geworfen, falls der Pfad je hitten wird —
          // tut er bei Firma/Admin-Rolle mit dringendem Auftrag durchaus).
          notifs.pushNotif(
            user.id,
            (tE) => ({ title: tE(titleKey), sub: body }),
            user.rolle === 'admin' ? 'admin-auftraege' : 'auftraege',
            'systemUpdate',
          );
        }
      });
    };
    check();
    const iv = setInterval(check, 15 * 60 * 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user && user.id, auth.auftraege.length]);

  // ═══ Zertifikat-Ablauf-Erinnerung (Fahrer, seit 150 auch Firma) ═══
  // Migration 150: Am Zertifikat „Transportversicherung“ hängt jetzt auch
  // das Bieten von Firmen — der Chef wird deshalb ebenfalls erinnert.
  useEffect(() => {
    if (!user || (user.rolle !== 'fahrer' && user.rolle !== 'firma')) return;
    const warnKey = 'drpx_zert_warn_' + todayLocal();
    if (sessionStorage.getItem(warnKey)) return;
    db.getFahrerZertifikate(user.id)
      .then((r) => {
        if (!r.data || r.data.length === 0) return;
        // 23.10.2026: gleiche Regel wie der Dashboard-Hinweis (`zertAblaufListe`):
        // nur geprüfte Nachweise, „gültig bis“ = letzter gültiger Tag (vorher
        // galt ein Nachweis schon am letzten Tag als abgelaufen und solche in
        // Prüfung zählten mit).
        const warnungen: ZertWarnung[] = zertAblaufListe(
          r.data as Array<Record<string, unknown>>,
          todayLocal(),
        ).map((e) => ({ key: e.zertKey, typ: e.abgelaufen ? 'abgelaufen' : 'bald' }));
        if (warnungen.length > 0) {
          sessionStorage.setItem(warnKey, '1');
          const abgelaufen = warnungen.filter((w) => w.typ === 'abgelaufen');
          const bald = warnungen.filter((w) => w.typ === 'bald');
          if (abgelaufen.length > 0) {
            // BUG #35 FIX (siehe oben): `notifications` → `notifs`
            notifs.pushNotif(
              user.id,
              (tE) => ({
                title: tE('zertAbgelaufenTitle'),
                sub: abgelaufen.length === 1
                  ? tE('zertAbgelaufenBodyEins')
                  : tE('zertAbgelaufenBodyMehr').replace('{n}', String(abgelaufen.length)),
              }),
              'zertifikate',
              'zertifikatUpdate',
            );
          }
          if (bald.length > 0) {
            notifs.pushNotif(
              user.id,
              (tE) => ({
                title: tE('zertBaldTitle'),
                sub: bald.length === 1
                  ? tE('zertBaldBodyEins')
                  : tE('zertBaldBodyMehr').replace('{n}', String(bald.length)),
              }),
              'zertifikate',
              'zertifikatUpdate',
            );
          }
        }
      })
      .catch(() => {
        /* silent */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user && user.id]);

  // ═══ AppContext (for Pages + AppShell using useApp()) ═══
  // NOTE: Der Cast via `unknown` ist nötig, weil `AppContextType` in
  // `contexts/AppContext.tsx` spezifisch typisiert ist und ich hier den
  // gebündelten Shape aus Hook-Returns zusammenstelle. TS kann die
  // Struktur-Identität nicht automatisch herleiten (siehe Handover Pattern 25).
  const appCtx = useMemo(
    () =>
      ({
        // ── Auth & Data ──
        user,
        users: auth.users,
        setUsers: auth.setUsers,
        setCurrentUser: auth.setCurrentUser,
        auftraege: auth.auftraege,
        setAuftraege: auth.setAuftraege,
        bewertungen: auth.bewertungen,
        setBewertungen: auth.setBewertungen,
        dismissedAngeboteIds: auth.dismissedAngeboteIds,
        setDismissedAngeboteIds: auth.setDismissedAngeboteIds,
        logout: auth.logout,
        // ── Navigation ──
        tab: navigation.tab,
        onNav: navigation.nav,
        navTabs: navigation.navTabs,
        navBadges: navigation.navBadges,
        mobileMenu: navigation.mobileMenu,
        setMobileMenu: navigation.setMobileMenu,
        bottomSheet: navigation.bottomSheet,
        setBottomSheet: navigation.setBottomSheet,
        chatUngelesen: navigation.chatUngeleseneAktiv,
        ladeChatUngelesen: navigation.ladeChatUngelesen,
        // ── Notifications ──
        pushNotif: notifs.pushNotif,
        notifOpen: notifs.notifOpen,
        setNotifOpen: notifs.setNotifOpen,
        unreadCount: notifs.unreadCount,
        meineNotifs: notifs.meineNotifs,
        adminMsgs: notifs.adminMsgs,
        markAllRead: notifs.markAllRead,
        markAdminMsgRead: notifs.markAdminMsgRead,
        deleteAdminMsg: notifs.deleteAdminMsg,
        clickNotif: notifs.clickNotif,
        liveBanner: notifs.liveBanner,
        setLiveBanner: notifs.setLiveBanner,
        // ── Settings ──
        darkMode: settings.darkMode,
        themeMode: settings.themeMode,
        cycleThemeMode: settings.cycleThemeMode,
        neutralBg: settings.neutralBg,
        toggleNeutralBg: settings.toggleNeutralBg,
        lang: settings.lang,
        setLang: settings.setLang,
        langOpen: settings.langOpen,
        setLangOpen: settings.setLangOpen,
        LANGS: settings.LANGS,
        LANG_LABELS: settings.LANG_LABELS,
        LANG_FLAGS: settings.LANG_FLAGS,
        LANG_NAMES: settings.LANG_NAMES,
        cookieConsent: settings.cookieConsent,
        handleCookieConsent: settings.handleCookieConsent,
        emailNotifs: settings.emailNotifs,
        toggleEmailNotifs: settings.toggleEmailNotifs,
        onboardingStep: settings.onboardingStep,
        setOnboardingStep: settings.setOnboardingStep,
        finishOnboarding: settings.finishOnboarding,
        // ── Social ──
        blockUser: social.blockUser,
        isBlocked: social.isBlocked,
        toggleFavorit: social.toggleFavorit,
        isFavorit: social.isFavorit,
        // ── UI State ──
        openToast,
        onOpenModal: openModal,
        t,
        toast,
        setToast,
        modal,
        setModal,
        onZertUpdate: auth.setPendingZertsTotal,
        pendingZertsTotal: auth.pendingZertsTotal,
        setPendingZertsTotal: auth.setPendingZertsTotal,
      }) as unknown as Parameters<typeof AppProvider>[0]['value'],
    [user, auth, navigation, notifs, settings, social, t, toast, modal, openToast, openModal],
  );

  // ═══ Hash-Migration: alte #track= Links → /track/:token ═══
  useEffect(() => {
    const m = window.location.hash.match(/^#track=(.+)$/);
    if (m) {
      navigate('/track/' + decodeURIComponent(m[1]), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ═══ OFFLINE INDICATOR ═══
  useEffect(() => {
    const onOnline = (): void => {
      const el = document.getElementById('offline-bar');
      if (el) el.style.display = 'none';
    };
    const onOffline = (): void => {
      if (!document.getElementById('offline-bar')) {
        const bar = document.createElement('div');
        bar.id = 'offline-bar';
        bar.style.cssText =
          'position:fixed;top:0;left:0;right:0;background:#f59e0b;color:#fff;text-align:center;padding:4px;font-size:11px;font-weight:700;z-index:99999';
        bar.textContent = 'Offline \u2014 Daten werden lokal gespeichert';
        document.body.appendChild(bar);
      } else {
        const el = document.getElementById('offline-bar');
        if (el) el.style.display = 'block';
      }
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // ═══ PRE-AUTH SCREENS ═══

  // Wartungsmodus
  if (WARTUNG_PASSWORT && !wartungFreigeschaltet) {
    return (
      <WartungsScreen
        wartungInput={wartungInput}
        // useSettings liefert `(v: string) => void`, WartungsScreen erwartet
        // aber `Dispatch<SetStateAction<string>>`. Cast nötig (Pattern 25).
        setWartungInput={
          setWartungInput as unknown as Dispatch<SetStateAction<string>>
        }
        wartungPruefen={wartungPruefen}
        t={t}
      />
    );
  }

  // Loading
  if (auth.loading) {
    return <LoadingScreen darkMode={darkMode} t={t} />;
  }

  // ═══ ROUTES ═══
  // Team-Einladung (DRPX-Pro, Migration 141): Link aus der Mail
  // (/einladung?token_hash=…&type=invite). Passwort festlegen + Datenschutz
  // bestätigen; danach neu laden auf „/" — der normale Start meldet den
  // jetzt aktiven Mitarbeiter an. Eine vorher im Browser bestehende Sitzung
  // wird dabei ersetzt (wie beim Passwort-Link).
  if (auth.einladungToken) {
    return (
      <I18nContext.Provider value={t}>
        <div
          style={{
            fontFamily: "'DM Sans',sans-serif",
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            ...(getThemeVars(darkMode, settings.neutralBg) as CSSProperties),
          }}>
          <EinladungAnnehmen
            tokenHash={auth.einladungToken}
            onComplete={() => window.location.replace('/')}
          />
        </div>
      </I18nContext.Provider>
    );
  }

  // Password Recovery: Formular „Neues Passwort" — sofort beim Aufruf des
  // Links aus der Mail (/recovery, `auth.recoveryToken`; eingelöst wird er
  // erst beim Speichern, 17.10.2026) oder wenn Supabase PASSWORD_RECOVERY
  // meldet. Nach dem Speichern komplett neu laden auf „/": Dann übernimmt
  // der normale Start mit der neuen Sitzung (handleLogin → Startseite der
  // Rolle). Das ist auch richtig, falls im Browser vorher jemand anderes
  // angemeldet war — die Sitzung gehört jetzt dem Link-Inhaber.
  if (auth.passwordRecovery || auth.recoveryToken) {
    return (
      <I18nContext.Provider value={t}>
        <div
          style={{
            fontFamily: "'DM Sans',sans-serif",
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...(getThemeVars(darkMode, settings.neutralBg) as CSSProperties),
          }}>
          <PasswordRecoveryForm
            _darkMode={darkMode}
            tokenHash={auth.recoveryToken}
            onComplete={() => window.location.replace('/')}
          />
        </div>
      </I18nContext.Provider>
    );
  }

  // Soft-Delete Karenz-Screen (DSGVO Art. 17)
  if (auth.pendingDeletion) {
    return (
      <I18nContext.Provider value={t}>
        <PendingDeletionScreen
          user={auth.pendingDeletion}
          darkMode={darkMode}
          onLogout={auth.logout}
          t={t}
        />
      </I18nContext.Provider>
    );
  }

  return (
    <I18nContext.Provider value={t}>
      <AppProvider value={appCtx}>
        <Routes>
          {/* ═══ Public: Tracking (kein Login nötig) ═══ */}
          <Route
            path="/track/:token"
            element={
              <Suspense fallback={null}>
                <TrackingPage />
              </Suspense>
            }
          />

          {/* ═══ Login ═══ */}
          <Route
            path="/login"
            element={
              auth.currentUser ? (
                <Navigate
                  to={startPath(user?.rolle)}
                  replace={true}
                />
              ) : (
                <div
                  style={{
                    fontFamily: "'DM Sans',sans-serif",
                    minHeight: '100vh',
                    ...(getThemeVars(darkMode, settings.neutralBg) as CSSProperties),
                  }}>
                  {auth.sbError && (
                    <div
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 9999,
                        background: 'rgba(239,68,68,.95)',
                        color: '#fff',
                        padding: '12px 20px',
                        fontSize: 13,
                        textAlign: 'center',
                        backdropFilter: 'blur(8px)',
                      }}>
                      {auth.sbError}
                      <br />
                      <span style={{ fontSize: 11, opacity: 0.7 }}>
                        SQL-Schema in Supabase ausgeführt? Tabelle "profiles" vorhanden?
                      </span>
                    </div>
                  )}
                  <AuthScreen
                    // useAuth liefert `Promise<AuthActionResult>`, AuthScreen
                    // gibt das Ergebnis durch bis an useLoginForm/useRegisterForm,
                    // damit die Fehler im UI angezeigt werden können
                    // (z.B. "email_not_confirmed" mit Resend-Button).
                    onLogin={async (email, password) => {
                      return await auth.login(email, password);
                    }}
                    onRegister={async (data) => {
                      // useRegisterForm.RegisterData und useAuth.RegisterData sind
                      // strukturell ähnlich aber nicht identisch (z.B. hat
                      // useRegisterForm ein `name`-Feld, useAuth hat optionale
                      // Markierungen). Cast via unknown (Pattern 25).
                      return await auth.register(data as unknown as Parameters<typeof auth.register>[0]);
                    }}
                    onResetPassword={async (email) => {
                      return await auth.resetPassword(email);
                    }}
                    onResendConfirmation={async (email) => {
                      return await auth.resendConfirmation(email);
                    }}
                    langOpen={settings.langOpen}
                    // Dispatch-Cast analog WartungsScreen (Pattern 25)
                    setLangOpen={
                      settings.setLangOpen as unknown as Dispatch<SetStateAction<boolean>>
                    }
                    lang={settings.lang}
                    setLang={settings.setLang}
                    LANGS={settings.LANGS}
                    LANG_LABELS={settings.LANG_LABELS}
                    LANG_FLAGS={settings.LANG_FLAGS}
                    LANG_NAMES={settings.LANG_NAMES}
                    darkMode={darkMode}
                    themeMode={settings.themeMode}
                    cycleThemeMode={settings.cycleThemeMode}
                  />
                  {!settings.cookieConsent && (
                    <CookieBanner
                      // useSettings definiert `CookieConsent` mit `essential: boolean`,
                      // CookieBanner erwartet Version mit `required: true`. Die zwei
                      // Typen leben parallel — Cast via unknown (Pattern 25).
                      onAccept={
                        settings.handleCookieConsent as unknown as Parameters<
                          typeof CookieBanner
                        >[0]['onAccept']
                      }
                    />
                  )}
                </div>
              )
            }
          />

          {/* ═══ Protected: Alles andere ═══ */}
          <Route
            path="/*"
            element={
              !auth.currentUser ? (
                <Navigate to="/login" replace={true} />
              ) : (
                // 24.05.2026: KycWaitScreen entfernt. User bleibt immer in der
                // normalen App, auch wenn kyc_status != 'approved'. Marktplatz-
                // Schutz (Fahrer ohne approved KYC sieht keine Aufträge) bleibt
                // unabhängig davon per RLS aktiv (Migration 047). Im Profil
                // sieht der User seinen KYC-Stand über VerifizierungBanner und
                // kann jederzeit Dokumente nachreichen.
                <AppShell>
                  <ProtectedRoutes />
                </AppShell>
              )
            }
          />
        </Routes>
      </AppProvider>
    </I18nContext.Provider>
  );
}
